import type { ActionFunctionArgs } from "react-router-dom";
import { redirect } from "react-router-dom";
import { authAPI, APIError } from "../../lib/api";
import {
  loginSchema,
  registerSchema,
  forgotPasswordSchema,
  resetPasswordSchema,
} from "../../schemas";
import { extractFieldErrors, parseFormData } from "../../utils";

export interface ActionResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  fieldErrors?: Record<string, string>;
}

export async function loginAction({
  request,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const formData = await parseFormData(request);
  const result = loginSchema.safeParse(formData);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    const response = await authAPI.login(result.data);
    return { success: true, data: response };
  } catch (error) {
    if (error instanceof APIError) {
      // Status 0 means the request never reached the server (network failure,
      // backend unreachable, etc.) — treat it the same as a non-API error.
      if (error.statusCode === 0) {
        return {
          success: false,
          error: "Unable to connect. Please check your internet connection and try again.",
        };
      }
      // Use the server-provided message for known error statuses;
      // for truly unexpected 500s, give a friendlier fallback.
      if (error.statusCode === 500) {
        return {
          success: false,
          error: "Something went wrong on our end. Please try again later.",
        };
      }
      return { success: false, error: error.message };
    }
    return {
      success: false,
      error: "Unable to connect. Please check your internet connection and try again.",
    };
  }
}

export async function registerAction({
  request,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const url = new URL(request.url);
  const returnUrl = url.searchParams.get("returnUrl");
  const formData = await parseFormData(request);
  const result = registerSchema.safeParse(formData);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    const response = await authAPI.register({
      email: result.data.email,
      password: result.data.password,
      organizationName: result.data.organizationName || undefined,
    });

    const returnParam = returnUrl ? `&returnUrl=${encodeURIComponent(returnUrl)}` : "";

    // If user was auto-verified (dev mode), skip the verify email screen
    if (response.user?.emailVerified) {
      return redirect(`/login?verified=true${returnParam}`);
    }

    return redirect(`/verify-email?registered=true${returnParam}`);
  } catch (error) {
    if (error instanceof APIError) {
      // Network errors (request never reached the server)
      if (error.statusCode === 0) {
        return {
          success: false,
          error: "Unable to connect. Please check your internet connection and try again.",
        };
      }

      // Unexpected server errors
      if (error.statusCode === 500) {
        return {
          success: false,
          error: "Something went wrong on our end. Please try again later.",
        };
      }

      // Map known server errors to specific fields
      const msg = error.message.toLowerCase();
      if (msg.includes("organization name")) {
        return { success: false, fieldErrors: { organizationName: error.message } };
      }
      if (msg.includes("email already") || msg.includes("email exists")) {
        return { success: false, fieldErrors: { email: error.message } };
      }

      // Server-side validation errors may contain "field: message" pairs
      // (e.g. from Zod: "password: Password must be at least 8 characters, email: Invalid")
      const fieldErrors = parseServerValidationErrors(error.message);
      if (fieldErrors) {
        return { success: false, fieldErrors };
      }

      return { success: false, error: error.message };
    }
    return {
      success: false,
      error: "Unable to connect. Please check your internet connection and try again.",
    };
  }
}

/**
 * Attempt to parse comma-separated "field: message" pairs from a server
 * validation error string (e.g. "password: Must be 8+ chars, email: Invalid").
 * Returns a field-error map if at least one known field is found, else null.
 */
const KNOWN_REGISTER_FIELDS = new Set(["email", "password", "organizationname"]);

function parseServerValidationErrors(message: string): Record<string, string> | null {
  const parts = message.split(",").map((s) => s.trim());
  const errors: Record<string, string> = {};
  let matched = false;

  for (const part of parts) {
    const colonIdx = part.indexOf(":");
    if (colonIdx < 1) continue;
    const field = part.slice(0, colonIdx).trim().toLowerCase();
    const msg = part.slice(colonIdx + 1).trim();
    if (KNOWN_REGISTER_FIELDS.has(field) && msg) {
      // Map to camelCase field names used by the form
      const fieldName = field === "organizationname" ? "organizationName" : field;
      errors[fieldName] = msg;
      matched = true;
    }
  }

  return matched ? errors : null;
}

export async function forgotPasswordAction({ request }: ActionFunctionArgs): Promise<ActionResult> {
  const formData = await parseFormData(request);
  const result = forgotPasswordSchema.safeParse(formData);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    await authAPI.forgotPassword(result.data);
    return { success: true };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function resetPasswordAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const { token } = params;

  if (!token) {
    return { success: false, error: "Reset token is required" };
  }

  const formData = await parseFormData(request);
  const dataWithToken = { ...formData, token };
  const result = resetPasswordSchema.safeParse(dataWithToken);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    await authAPI.resetPassword({
      token,
      password: result.data.password,
    });
    return redirect("/login?reset=true");
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export interface ResendVerificationResult {
  success: boolean;
  error?: string;
  redirect?: string;
}

export async function resendVerificationAction(): Promise<ResendVerificationResult> {
  try {
    await authAPI.resendVerification();
    return { success: true };
  } catch (error) {
    if (error instanceof APIError) {
      if (error.statusCode === 401) {
        return {
          success: false,
          error: "Please log in first to resend verification email",
          redirect: "/login",
        };
      }
      if (error.statusCode === 429) {
        return {
          success: false,
          error: "Too many requests. Please try again later.",
        };
      }
      return { success: false, error: error.message || "Failed to resend verification email" };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}
